<!-- Main Container -->
<div class="dashboard-container">
  <!-- Header Section -->
  <div class="dashboard-header">
    <div class="header-content">
      <h4 class="mb-0">Event Monitoring</h4>
      <div class="date-picker-container">
        <input type="text" class="form-control datepicker" [value]="currentDate.dateStr" placeholder="Select date">
        <i class="fas fa-calendar-alt"></i>
      </div>
    </div>
  </div>

  <!-- Loading and Error States -->
  <div *ngIf="currentDate.loading" class="loading-container">
    <div class="spinner">
      <div class="bounce1"></div>
      <div class="bounce2"></div>
      <div class="bounce3"></div>
    </div>
    <p>Loading events...</p>
  </div>

  <div *ngIf="currentDate.error" class="error-container">
    <div class="error-message">
      <i class="fas fa-exclamation-circle"></i>
      <span>{{ currentDate.error }}</span>
    </div>
  </div>

  <!-- Events Table View -->
  <div *ngIf="!currentDate.loading && !currentDate.error" class="events-table-container">
    <table class="events-table">
      <thead>
        <tr>
          <th>Venue Name</th>
          <th>Event ID</th>
          <th>Status</th>
          <th>Prepare Status</th>
          <th>Network Status</th>
        </tr>
      </thead>
      <tbody>
        <ng-container *ngFor="let event of currentDate.events">
          <tr [ngClass]="{'highlight': event.showDetail}">
            <td>
              <div class="venue-name" (click)="toggleEventDetail(event)">
                <div class="led-indicator" [ngClass]="event.race_preparation?.all_completed ? 'success' : 'failed'"></div>
                {{ event.venue_name }}
                <i class="fas" [ngClass]="event.showDetail ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
              </div>
            </td>
            <td>{{ event.external_event_id }}</td>
            <td>
              <div class="status-badge"
                [ngClass]="getStatusClass(event.race_preparation?.status?.event_state?.status || 'PLANNED')">
                {{ event.race_preparation?.status?.event_state?.status || 'PLANNED' }}
              </div>
            </td>
            <td>
              <div class="prepare-summary">
                <div class="prepare-progress">
                  <span class="progress-label">Race Prep:</span>
                  <span class="progress-value">{{ event.race_preparation?.completion_percentage || 0 }}%</span>
                </div>
                <div class="progress-bar">
                  <div class="progress" [style.width.%]="event.race_preparation?.completion_percentage || 0"
                    [style.background-color]="getProgressColor(event.race_preparation?.completion_percentage || 0)">
                  </div>
                </div>
              </div>
            </td>
            <td>
              <div class="network-summary">
                <span class="online">
                  <i class="fas fa-check-circle"></i>
                  {{ getNetworkStatusCount(event.network_status, '1') }} Online
                </span>
                <span class="offline">
                  <i class="fas fa-times-circle"></i>
                  {{ getNetworkStatusCount(event.network_status, '0') }} Offline
                </span>
              </div>
            </td>
          </tr>
          <!-- Event Details Row -->
          <tr *ngIf="event.showDetail" class="details-row">
            <td colspan="5">
              <div class="event-details-container">
                <!-- Details Row Section -->
                <div class="details-row-section">
                  <!-- Event Info -->
                  <div class="detail-card event-info">
                    <h6>Race Preparation</h6>
                    <div class="detail-info">
                      <div class="info-item">
                        <span class="label">Event ID:</span>
                        <span class="value">{{ event.external_event_id }}</span>
                      </div>
                      <div class="info-item">
                        <span class="label">PC:</span>
                        <div class="status-display">
                          <div class="led-indicator" [ngClass]="event.race_preparation?.status?.event_state?.assigned_pc ? 'success' : 'failed'"></div>
                          <span class="value">{{ event.race_preparation?.status?.event_state?.assigned_pc || '-' }}</span>
                        </div>
                      </div>
                      <div class="info-item">
                        <span class="label">Operator:</span>
                        <div class="status-display">
                          <div class="led-indicator" [ngClass]="event.race_preparation?.status?.event_state?.assigned_operator ? 'success' : 'failed'"></div>
                          <span class="value">{{ event.race_preparation?.status?.event_state?.assigned_operator || '-' }}</span>
                        </div>
                      </div>
                      <div class="info-item">
                        <span class="label">Track Assignments:</span>
                        <div class="status-display">
                          <div class="led-indicator" [ngClass]="event.race_preparation?.status?.track_assignments?.complete ? 'success' : 'failed'"></div>
                          <span class="value">{{ event.race_preparation?.status?.track_assignments?.tracks_assigned || 0 }}/{{ event.race_preparation?.status?.track_assignments?.total_races || 0 }}</span>
                        </div>
                      </div>
                      <div class="info-item">
                        <span class="label">Rail Positions:</span>
                        <div class="status-display">
                          <div class="led-indicator" [ngClass]="event.race_preparation?.status?.rail_position?.complete ? 'success' : 'failed'"></div>
                          <span class="value">{{ event.race_preparation?.status?.rail_position?.rail_positions_set || 0 }}</span>
                        </div>
                      </div>
                      <div class="info-item">
                        <span class="label">Tag Kit:</span>
                        <div class="status-display">
                          <div class="led-indicator" [ngClass]="event.race_preparation?.status?.kit_assignment?.complete ? 'success' : 'failed'"></div>
                          <span class="value">{{ event.race_preparation?.status?.kit_assignment?.kit_id || '-' }}</span>
                        </div>
                      </div>
                      <div class="info-item">
                        <span class="label">Scratchings:</span>
                        <div class="status-display">
                          <div class="led-indicator" [ngClass]="event.race_preparation?.status?.scratchings?.complete ? 'success' : 'failed'"></div>
                          <span class="value">{{ event.race_preparation?.status?.scratchings?.completed || 0 }}/{{ event.race_preparation?.status?.scratchings?.total_races || 0 }}</span>
                        </div>
                      </div>
                      <div class="info-item" *ngIf="event.race_preparation?.status?.scratchings?.failed">
                        <span class="label">Failed Scratchings:</span>
                        <div class="status-display">
                          <div class="led-indicator failed"></div>
                          <span class="value status-failed">{{ event.race_preparation?.status?.scratchings?.failed }}</span>
                        </div>
                      </div>

                      <!-- Start List Section -->
                      <div class="info-item">
                        <span class="label">Start List:</span>
                        <div class="status-display">
                          <div class="led-indicator" [ngClass]="event.race_preparation?.status?.startlist?.pdf_generated ? 'success' : 'failed'"></div>
                          <span class="value">{{ event.race_preparation?.status?.startlist?.pdf_generated ? 'Generated' : 'Pending' }}</span>
                          <ng-container *ngIf="event.race_preparation?.status?.startlist?.pdf_generated">
                            <a [href]="event.race_preparation?.status?.startlist?.pdf_file_path"
                               target="_blank"
                               class="start-list-link">
                              <i class="fas fa-file-pdf"></i>
                              View PDF
                            </a>
                            <div class="led-indicator" [ngClass]="event.race_preparation?.status?.startlist?.email_sent ? 'success' : 'failed'"></div>
                            <span>Email {{ event.race_preparation?.status?.startlist?.email_sent ? 'Sent' : 'Pending' }}</span>
                          </ng-container>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Network Status Section -->
                  <div class="detail-card network-status-section">
                    <h6>Network Status</h6>
                    <div class="detail-info">
                      <div *ngFor="let device of getSortedNetworkDevices(event.network_status)" class="info-item">
                        <span class="label">
                          <div class="flex-start">
                            <div class="led-indicator" [ngClass]="getDeviceStatus(device.status)"></div>
                            <span>{{ device.name }}</span>
                          </div>
                        </span>
                        <div class="status-display">
                          <div class="device-info">
                            <div class="device-meta">
                              <span class="ip">{{ device.instance }}</span>
                              <span class="tag job">{{ device.job }}</span>
                              <span class="tag service">{{ device.service }}</span>
                            </div>
                            <div class="device-status">{{ device.status_message }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>


              </div>
            </td>
          </tr>
        </ng-container>
      </tbody>
    </table>
  </div>
</div>

<!-- Add this at the end of the template -->
<div class="custom-tooltip" #tooltip>
  <div class="tooltip-content"></div>
  <div class="tooltip-arrow"></div>
</div>
